<template>
  <div class="cs" v-click-outside="fechar">
    <div class="cs-search-wrapper">
      <fa-icon icon="search" class="cs-search-icon" />
      <input
        ref="searchInput"
        type="text"
        class="cs-search-input"
        :placeholder="placeholder"
        :value="value"
        @input="onInput"
        @focus="abrir"
        @keydown.down.prevent="navegarParaBaixo"
        @keydown.up.prevent="navegarParaCima"
        @keydown.enter.prevent="selecionarAtivo"
        @keydown.esc.prevent="fechar"
      />
      <span v-if="value" class="cs-search-clear" @click="limpar">
        <fa-icon icon="times" />
      </span>
    </div>

    <div v-if="aberto" class="cs-panel" :style="estiloPanel" ref="panel">
      <div class="cs-counter">
        {{ totalFiltrado }} de {{ totalCampos }} campo(s) — você também pode digitar um nome customizado.
      </div>

      <div class="cs-body">
        <div v-if="totalFiltrado === 0" class="cs-empty">
          <fa-icon icon="search" class="cs-empty-icon" />
          <div class="cs-empty-text">Nenhum campo encontrado</div>
          <div class="cs-empty-desc">O valor digitado será usado como campo customizado.</div>
        </div>

        <!-- Raiz "Campos" — abre a árvore de submodels (Pedido, Cliente, etc). -->
        <div v-if="totalFiltrado > 0" class="cs-group">
          <div class="cs-group-header" @click="toggleRaiz">
            <fa-icon
              :icon="raizExpandidaEfetiva ? 'chevron-down' : 'chevron-right'"
              class="cs-group-chevron"
            />
            <span class="cs-group-label">Campos</span>
            <span class="cs-group-count">{{ totalFiltrado }}</span>
          </div>

          <div v-show="raizExpandidaEfetiva" class="cs-group-items">
            <div v-for="(grupo, nomeGrupo) in gruposFiltrados" :key="nomeGrupo" class="cs-subgroup">
              <div class="cs-subgroup-header" @click="toggleGrupo(nomeGrupo)">
                <fa-icon
                  :icon="grupoExpandido(nomeGrupo) ? 'chevron-down' : 'chevron-right'"
                  class="cs-subgroup-chevron"
                />
                <span class="cs-subgroup-label">{{ nomeGrupo }}</span>
                <span class="cs-subgroup-count">{{ grupo.length }}</span>
              </div>

              <div v-show="grupoExpandido(nomeGrupo)" class="cs-subgroup-items">
                <div
                  v-for="campo in grupo"
                  :key="campo.id"
                  class="cs-item"
                  :class="{ 'cs-item--active': campo.globalIndex === indiceFocado }"
                  :ref="'item-' + campo.globalIndex"
                  @click="selecionar(campo.id)"
                  @mouseenter="indiceFocado = campo.globalIndex"
                >
                  <div class="cs-item-info">
                    <div class="cs-item-label" v-html="destacar(campo.label)"></div>
                    <code class="cs-item-id" v-html="destacar(campo.id)"></code>
                  </div>
                  <fa-icon icon="arrow-right" class="cs-item-arrow" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const clickOutside = {
  bind(el, binding) {
    el.__cs_click__ = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener('mousedown', el.__cs_click__);
  },
  unbind(el) {
    document.removeEventListener('mousedown', el.__cs_click__);
    delete el.__cs_click__;
  },
};

export default {
  name: 'AcaoObjetoCampoSelect',
  directives: { 'click-outside': clickOutside },
  props: {
    value: { type: String, default: '' },
    fields: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Buscar ou digitar campo customizado...' },
  },
  data() {
    return {
      aberto: false,
      indiceFocado: -1,
      raizExpandida: true,
      gruposAbertos: {},
      panelPos: { top: 0, left: 0, width: 0, openUp: false, maxHeight: 320 },
    };
  },
  computed: {
    camposArray() {
      return (this.fields || []).map(f => ({
        id:    f.name,
        label: f.label || f.name,
        // O backend já entrega o segmento (model relacionada) em `grupo`.
        // Mantemos fallback para a heurística de prefixo só por compat.
        grupo: f.grupo || null,
      }));
    },
    camposAgrupados() {
      const grupos = {};
      this.camposArray.forEach(campo => {
        const nome = campo.grupo || this.detectarGrupo(campo.id);
        if (!grupos[nome]) grupos[nome] = [];
        grupos[nome].push(campo);
      });
      return grupos;
    },
    gruposFiltrados() {
      const termo = (this.value || '').toLowerCase().trim();
      const resultado = {};
      let globalIndex = 0;

      const ordem = Object.keys(this.camposAgrupados).sort((a, b) => {
        // "Campos do Evento" (payload bruto) sempre primeiro; "Campos Adicionais"/"Outros" sempre por último.
        if (a === 'Campos do Evento') return -1;
        if (b === 'Campos do Evento') return 1;
        if (a === 'Campos Adicionais' || a === 'Outros') return 1;
        if (b === 'Campos Adicionais' || b === 'Outros') return -1;
        return a.localeCompare(b);
      });

      ordem.forEach(nome => {
        const campos = this.camposAgrupados[nome];
        const filtrados = termo
          ? campos.filter(c =>
              c.label.toLowerCase().includes(termo) ||
              c.id.toLowerCase().includes(termo)
            )
          : campos;

        if (filtrados.length > 0) {
          resultado[nome] = filtrados.map(c => ({ ...c, globalIndex: globalIndex++ }));
        }
      });

      return resultado;
    },
    totalFiltrado() {
      return Object.values(this.gruposFiltrados).reduce((s, g) => s + g.length, 0);
    },
    totalCampos() {
      return this.camposArray.length;
    },
    estiloPanel() {
      return {
        position: 'fixed',
        top: this.panelPos.top + 'px',
        left: this.panelPos.left + 'px',
        width: this.panelPos.width + 'px',
        maxHeight: this.panelPos.maxHeight + 'px',
      };
    },
    todosVisiveis() {
      const arr = [];
      Object.values(this.gruposFiltrados).forEach(g => g.forEach(c => arr.push(c)));
      return arr;
    },
    raizExpandidaEfetiva() {
      // Durante busca a raiz fica sempre aberta para revelar os matches.
      const filtrando = !!(this.value || '').trim();
      return filtrando || this.raizExpandida;
    },
  },
  beforeDestroy() {
    this.removerListeners && this.removerListeners();
  },
  methods: {
    detectarGrupo(id) {
      // Fallback: usado apenas quando o backend não fornece `grupo` (compat
      // com fontes legadas). Hoje o EventoCamposBuilder sempre entrega o
      // segmento, então isso só roda em consumidores antigos do componente.
      if (id.startsWith('campoAdicional.') || id.startsWith('camposAdicionais.')) {
        return 'Campos Adicionais';
      }
      return 'Outros';
    },
    toggleRaiz() {
      this.raizExpandida = !this.raizExpandida;
    },
    grupoExpandido(nome) {
      // Durante a busca, abrimos todos os grupos com resultado para que o
      // usuário veja os matches sem precisar clicar. Sem busca, fechado por
      // padrão — só abre se o usuário expandiu manualmente.
      const filtrando = !!(this.value || '').trim();
      if (filtrando) return true;
      return this.gruposAbertos[nome] === true;
    },
    toggleGrupo(nome) {
      // Ao filtrar (com termo de busca), o grupo já está aberto implicitamente
      // — o toggle nesse caso não faz sentido. Caso contrário, alterna estado
      // explicitamente: undefined/false → true, true → false.
      const atual = this.gruposAbertos[nome];
      this.$set(this.gruposAbertos, nome, atual === true ? false : true);
    },
    abrir() {
      this.aberto = true;
      this.atualizarPosicao();
      this.$nextTick(() => this.atualizarPosicao());
      this._onReposition = () => this.atualizarPosicao();
      window.addEventListener('scroll', this._onReposition, true);
      window.addEventListener('resize', this._onReposition);
    },
    fechar() {
      this.aberto = false;
      this.indiceFocado = -1;
      this.removerListeners();
    },
    removerListeners() {
      if (this._onReposition) {
        window.removeEventListener('scroll', this._onReposition, true);
        window.removeEventListener('resize', this._onReposition);
        this._onReposition = null;
      }
    },
    atualizarPosicao() {
      const wrapper = this.$refs.searchInput && this.$refs.searchInput.parentElement;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const margin = 8;
      const desiredHeight = 320;
      const espacoAbaixo = window.innerHeight - rect.bottom - margin;
      const espacoAcima = rect.top - margin;
      const abrirParaCima = espacoAbaixo < 220 && espacoAcima > espacoAbaixo;
      const maxH = Math.max(180, Math.min(desiredHeight, abrirParaCima ? espacoAcima : espacoAbaixo));
      this.panelPos = {
        top: abrirParaCima ? Math.max(margin, rect.top - maxH - 4) : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        openUp: abrirParaCima,
        maxHeight: maxH,
      };
    },
    onInput(ev) {
      this.$emit('input', ev.target.value);
      this.aberto = true;
      this.indiceFocado = -1;
    },
    limpar() {
      this.$emit('input', '');
      this.indiceFocado = -1;
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus();
      });
    },
    selecionar(id) {
      this.$emit('input', id);
      this.fechar();
    },
    navegarParaBaixo() {
      this.aberto = true;
      if (this.totalFiltrado === 0) return;
      this.indiceFocado = (this.indiceFocado + 1) % this.totalFiltrado;
      this.scrollToFocado();
    },
    navegarParaCima() {
      this.aberto = true;
      if (this.totalFiltrado === 0) return;
      this.indiceFocado = this.indiceFocado <= 0 ? this.totalFiltrado - 1 : this.indiceFocado - 1;
      this.scrollToFocado();
    },
    selecionarAtivo() {
      const campo = this.todosVisiveis.find(c => c.globalIndex === this.indiceFocado);
      if (campo) {
        this.selecionar(campo.id);
      } else {
        this.fechar();
      }
    },
    scrollToFocado() {
      this.$nextTick(() => {
        const refKey = 'item-' + this.indiceFocado;
        const el = this.$refs[refKey];
        if (el && el[0]) el[0].scrollIntoView({ block: 'nearest' });
      });
    },
    destacar(texto) {
      const termo = (this.value || '').trim();
      const escaped = this.escapeHtml(texto);
      if (!termo) return escaped;
      const regex = new RegExp(`(${this.escapeRegex(this.escapeHtml(termo))})`, 'gi');
      return escaped.replace(regex, '<mark class="cs-highlight">$1</mark>');
    },
    escapeHtml(text) {
      const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
      return String(text).replace(/[&<>"']/g, m => map[m]);
    },
    escapeRegex(text) {
      return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
  },
};
</script>

<style scoped>
.cs { position: relative; }

.cs-search-wrapper { position: relative; display: flex; align-items: center; }
.cs-search-icon {
  position: absolute; left: 12px; color: #98a2b3; font-size: 12px; pointer-events: none;
}
.cs-search-input {
  width: 100%;
  padding: 7px 32px 7px 32px;
  border: 1px solid #dddce9;
  border-radius: 6px;
  font-size: 13px;
  color: #344054;
  background: #fff;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  height: 36px;
}
.cs-search-input:focus { border-color: #3a3e81; box-shadow: 0 0 0 3px rgba(58, 62, 129, 0.1); }
.cs-search-input::placeholder { color: #98a2b3; }
.cs-search-clear {
  position: absolute; right: 8px; color: #98a2b3; cursor: pointer;
  font-size: 11px; padding: 4px; border-radius: 4px; transition: all 0.15s;
}
.cs-search-clear:hover { color: #667085; background: #f2f4f7; }

.cs-panel {
  z-index: 1100;
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
  padding: 8px 10px 6px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.cs-counter { font-size: 11px; color: #98a2b3; padding: 2px 4px 8px; flex-shrink: 0; }
.cs-body {
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}

.cs-empty { text-align: center; padding: 24px 16px; }
.cs-empty-icon { font-size: 20px; color: #d0d5dd; margin-bottom: 8px; }
.cs-empty-text { font-weight: 600; font-size: 13px; color: #344054; }
.cs-empty-desc { font-size: 11px; color: #98a2b3; margin-top: 3px; }

.cs-group + .cs-group { border-top: 1px solid #f2f4f7; }
.cs-group-header {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 12px; background: #f8f9fa;
  cursor: pointer; user-select: none; transition: background 0.1s;
}
.cs-group-header:hover { background: #f2f4f7; }
.cs-group-chevron { font-size: 9px; color: #98a2b3; width: 10px; flex-shrink: 0; }
.cs-group-label {
  font-size: 10px; font-weight: 700; color: #667085;
  text-transform: uppercase; letter-spacing: 0.5px; flex: 1;
}
.cs-group-count {
  font-size: 9px; font-weight: 600; padding: 1px 6px;
  border-radius: 4px; border: 1px solid #d0d5dd; color: #667085; background: #fff;
}

.cs-group-items { padding: 2px 0; }

/* Subgrupos (2º nível: model relacionada — Pedido, Cliente etc.) */
.cs-subgroup + .cs-subgroup { border-top: 1px solid #f6f7f9; }
.cs-subgroup-header {
  display: flex; align-items: center; gap: 8px;
  padding: 5px 12px 5px 26px; background: #fcfcfd;
  cursor: pointer; user-select: none; transition: background 0.1s;
}
.cs-subgroup-header:hover { background: #f4f5f7; }
.cs-subgroup-chevron { font-size: 9px; color: #98a2b3; width: 10px; flex-shrink: 0; }
.cs-subgroup-label {
  font-size: 11px; font-weight: 600; color: #475467; flex: 1;
}
.cs-subgroup-count {
  font-size: 9px; font-weight: 600; padding: 1px 6px;
  border-radius: 4px; border: 1px solid #e4e7eb; color: #98a2b3; background: #fff;
}
.cs-subgroup-items { padding: 2px 0; }
.cs-subgroup-items .cs-item { padding-left: 46px; }

.cs-item {
  display: flex; align-items: center; gap: 10px;
  padding: 6px 12px 6px 30px; cursor: pointer; transition: background 0.1s;
}
.cs-item:hover, .cs-item--active { background: #f2f2f8; }
.cs-item--active { background: #eceaf3; }
.cs-item-info { flex: 1; min-width: 0; }
.cs-item-label { font-size: 12px; font-weight: 500; color: #344054; line-height: 1.3; }
.cs-item-id {
  font-size: 10px; color: #98a2b3; background: none; padding: 0;
  display: block; margin-top: 1px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cs-item--active .cs-item-label { color: #3a3e81; font-weight: 600; }
.cs-item--active .cs-item-id { color: #6b6fa6; }
.cs-item-arrow { font-size: 9px; color: transparent; flex-shrink: 0; transition: color 0.1s, transform 0.1s; }
.cs-item:hover .cs-item-arrow, .cs-item--active .cs-item-arrow { color: #3a3e81; }
.cs-item--active .cs-item-arrow { transform: translateX(2px); }

.cs-highlight { background: #fef3c7; color: #92400e; border-radius: 2px; padding: 0 1px; }
</style>
